import React, { useEffect, useState } from 'react';
import { holdingApi, portfolioApi, configApi } from '../services/api';

interface Holding {
  id: number;
  portfolio: string;
  ticker: string;
  exchange: string;
  sector: string | null;
  cost: string | null;
  balance: string | null;
  cap: string | null;
  action: string | null;
  note: string | null;
  fund: boolean | null;
  monitor: boolean | null;
  active: boolean | null;
}

interface Portfolio {
  id: number;
  portfolioName: string;
}

const EXCHANGES = ['NYSE', 'NASDAQ', 'NYSEARCA', 'NYSEAMERICAN', 'OTC', 'TSE', 'CVE', 'CSE'];

const emptyForm = {
  portfolio: '',
  ticker: '',
  exchange: 'NYSE',
  sector: '',
  cost: '',
  balance: '',
  cap: '',
  action: '',
  note: '',
  active: true,
};

const HoldingsPage: React.FC = () => {
  const [holdings, setHoldings] = useState<Holding[]>([]);
  const [portfolios, setPortfolios] = useState<Portfolio[]>([]);
  const [selectedPortfolio, setSelectedPortfolio] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [sectors, setSectors] = useState<string[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ ...emptyForm });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    portfolioApi.list().then((res) => setPortfolios(res.data)).catch(() => {});
    configApi.sectors().then((res) => { console.log('sectors response:', res.data); setSectors(res.data.map((s: any) => s.sector)); }).catch((e) => console.error('sectors fetch failed:', e));
  }, []);

  useEffect(() => {
    setLoading(true);
    holdingApi
      .list(selectedPortfolio || undefined)
      .then((res) => setHoldings(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [selectedPortfolio]);

  const totalCost = holdings.reduce((sum, h) => sum + parseFloat(h.cost || '0'), 0);
  const totalBalance = holdings.reduce((sum, h) => sum + parseFloat(h.balance || '0'), 0);

  const reload = () => {
    holdingApi.list(selectedPortfolio || undefined).then((res) => setHoldings(res.data)).catch(() => {});
  };

  const handleSave = async () => {
    if (!form.ticker.trim()) return;
    setSaving(true);
    try {
      const payload: any = {
        ticker: form.ticker.trim().toUpperCase(),
        exchange: form.exchange || 'US',
        active: form.active,
      };
      if (form.portfolio) payload.portfolio = form.portfolio;
      if (form.sector) payload.sector = form.sector;
      else payload.sector = null;
      if (form.cost) payload.cost = form.cost;
      if (form.balance) payload.balance = form.balance;
      if (form.cap) payload.cap = form.cap;
      if (form.action) payload.action = form.action;
      if (form.note) payload.note = form.note;
      await holdingApi.create(payload);
      setForm({ ...emptyForm });
      setShowForm(false);
      reload();
    } catch (e: any) {
      alert(e?.response?.data?.detail || 'Failed to create holding');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h4 style={styles.heading}>Holdings</h4>
        <div style={styles.searchBar}>
          <label>Portfolio: </label>
          <select
            value={selectedPortfolio}
            onChange={(e) => setSelectedPortfolio(e.target.value)}
            style={styles.select}
          >
            <option value="">All Portfolios</option>
            {portfolios.map((p) => (
              <option key={p.id} value={p.portfolioName}>{p.portfolioName}</option>
            ))}
          </select>
        </div>
        <button
          onClick={() => setShowForm(!showForm)}
          style={{ padding: '4px 12px', fontSize: '0.85em', cursor: 'pointer', backgroundColor: '#0069cc', color: '#fff', border: 'none', borderRadius: '3px' }}
        >
          {showForm ? 'Cancel' : '+ Add Holding'}
        </button>
      </div>

      {showForm && (
        <div style={{ backgroundColor: '#f5f9ff', border: '1px solid #0d58e6', padding: '10px', marginBottom: '8px', display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center', fontSize: '0.85em' }}>
          <label>Portfolio:
            <select value={form.portfolio} onChange={(e) => setForm({ ...form, portfolio: e.target.value })} style={styles.select}>
              <option value="">Default</option>
              {portfolios.map((p) => <option key={p.id} value={p.portfolioName}>{p.portfolioName}</option>)}
            </select>
          </label>
          <label>Ticker:
            <input value={form.ticker} onChange={(e) => setForm({ ...form, ticker: e.target.value })} style={{ width: '70px', padding: '2px 4px' }} placeholder="AAPL" />
          </label>
          <label>Exchange:
            <select value={form.exchange} onChange={(e) => setForm({ ...form, exchange: e.target.value })} style={styles.select}>
              {EXCHANGES.map((ex) => <option key={ex} value={ex}>{ex}</option>)}
            </select>
          </label>
          <label>Sector:
            <select value={form.sector} onChange={(e) => setForm({ ...form, sector: e.target.value })} style={styles.select}>
              <option value="">--</option>
              {sectors.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </label>
          <label>Cost:
            <input value={form.cost} onChange={(e) => setForm({ ...form, cost: e.target.value })} style={{ width: '80px', padding: '2px 4px' }} type="number" step="0.01" />
          </label>
          <label>Balance:
            <input value={form.balance} onChange={(e) => setForm({ ...form, balance: e.target.value })} style={{ width: '80px', padding: '2px 4px' }} type="number" step="0.01" />
          </label>
          <label>Action:
            <input value={form.action} onChange={(e) => setForm({ ...form, action: e.target.value })} style={{ width: '80px', padding: '2px 4px' }} placeholder="hold/buy" />
          </label>
          <label>Note:
            <input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} style={{ width: '120px', padding: '2px 4px' }} />
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
            <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} /> Active
          </label>
          <button onClick={handleSave} disabled={saving || !form.ticker.trim()} style={{ padding: '4px 14px', backgroundColor: '#26a69a', color: '#fff', border: 'none', borderRadius: '3px', cursor: 'pointer' }}>
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: '2em' }}>Loading...</div>
      ) : (
        <table id="dataTable" style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Ticker</th>
              <th style={styles.th}>Exchange</th>
              <th style={styles.th}>Sector</th>
              <th style={styles.th}>Cost</th>
              <th style={styles.th}>Balance</th>
              <th style={styles.th}>Gain/Loss</th>
              <th style={styles.th}>Action</th>
              <th style={styles.th}>Monitor</th>
              <th style={styles.th}>Active</th>
            </tr>
          </thead>
          <tbody>
            {holdings.map((h, idx) => {
              const cost = parseFloat(h.cost || '0');
              const balance = parseFloat(h.balance || '0');
              const gain = balance - cost;
              const gainPct = cost > 0 ? (gain / cost) * 100 : 0;
              const gainColor = gain >= 0 ? 'green' : 'red';
              const rowBg = idx % 2 === 0 ? '#fff' : '#e6f2ff';
              const inactive = h.active === false;
              return (
                <tr key={h.id} style={{ backgroundColor: rowBg, opacity: inactive ? 0.5 : 1 }}>
                  <td style={styles.td}><strong>{h.ticker}</strong></td>
                  <td style={styles.td}>{h.exchange}</td>
                  <td style={styles.td}>{h.sector || '-'}</td>
                  <td style={{ ...styles.td, textAlign: 'right' }}>{cost.toFixed(2)}</td>
                  <td style={{ ...styles.td, textAlign: 'right' }}>{balance.toFixed(2)}</td>
                  <td style={{ ...styles.td, textAlign: 'right', color: gainColor }}>
                    {gain.toFixed(2)} ({gainPct.toFixed(1)}%)
                  </td>
                  <td style={styles.td}>{h.action || '-'}</td>
                  <td style={styles.td}>{h.monitor ? 'Y' : '-'}</td>
                  <td style={{ ...styles.td, textAlign: 'center' }}>{h.active ? 'Y' : 'N'}</td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr style={{ backgroundColor: '#cce6ff', fontWeight: 'bold' }}>
              <td style={styles.td} colSpan={3}>Total ({holdings.length} holdings)</td>
              <td style={{ ...styles.td, textAlign: 'right' }}>{totalCost.toFixed(2)}</td>
              <td style={{ ...styles.td, textAlign: 'right' }}>{totalBalance.toFixed(2)}</td>
              <td style={{ ...styles.td, textAlign: 'right', color: totalBalance - totalCost >= 0 ? 'green' : 'red' }}>
                {(totalBalance - totalCost).toFixed(2)}
              </td>
              <td style={styles.td} colSpan={3}></td>
            </tr>
          </tfoot>
        </table>
      )}
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    padding: '1em 0.5em',
    backgroundColor: '#fff',
    minHeight: 'calc(100vh - 6em)',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '0.5em',
  },
  heading: {
    color: '#0069cc',
    marginTop: '1em',
    textAlign: 'center',
  },
  searchBar: {
    backgroundColor: '#ececec',
    padding: '4px 10px',
    fontSize: '0.8em',
  },
  select: {
    padding: '2px 4px',
    fontFamily: 'Arial, sans-serif',
    fontSize: '1em',
  },
  table: {
    width: '100%',
    marginTop: '1em',
    border: '2px solid #0d58e6',
    borderCollapse: 'collapse',
  },
  th: {
    border: '1px solid #0d58e6',
    padding: '4px 6px',
    backgroundColor: '#cce6ff',
    fontWeight: 'bold',
    fontSize: '0.85em',
    textAlign: 'center',
  },
  td: {
    border: '1px solid #0d58e6',
    padding: '3px 6px',
    fontSize: '0.85em',
  },
};

export default HoldingsPage;
